import { Link, useParams } from 'react-router-dom';
import Container from '@/components/base/Container';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Seo from '@/components/feature/Seo';
import StatusBadge from '@/components/base/StatusBadge';
import TopicCard from '@/components/base/TopicCard';
import { topics } from '@/mocks/topics';

export default function TopicDetail() {
  const { slug } = useParams();
  const topic = topics.find((item) => item.slug === slug);

  if (!topic) {
    return (
      <section className="bg-background-50 py-20">
        <Seo title="线索不存在 - 看料社" description="你访问的追踪线索不存在或已被移动，可以返回追踪列表查看全部内容。" />
        <Container>
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
            <i className="ri-error-warning-line text-4xl text-foreground-400"></i>
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">没有找到这条线索</h1>
            <p className="mt-3 max-w-md text-sm text-foreground-600">
              它可能还在整理中，或者链接已经变更。你可以回到追踪列表，看看其他正在跟进的线索。
            </p>
            <Link
              to="/tracking"
              className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
            >
              <i className="ri-list-check-2"></i>
              返回追踪列表
            </Link>
          </div>
        </Container>
      </section>
    );
  }

  const related = topics.filter((item) => item.slug !== topic.slug && item.category === topic.category);
  const fallback = topics.filter((item) => item.slug !== topic.slug && item.category !== topic.category);
  const recommend = [...related, ...fallback].slice(0, 3);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const pageUrl = `${origin}/topic/${topic.slug}`;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: topic.title,
    description: topic.summary,
    inLanguage: 'zh-CN',
    datePublished: topic.publishedAt,
    dateModified: topic.updatedAt,
    articleSection: topic.categoryName,
    keywords: topic.tags.join(','),
    url: pageUrl,
    author: { '@type': 'Organization', name: '看料社' },
    publisher: { '@type': 'Organization', name: '看料社' },
    image: topic.cover,
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: origin },
      { '@type': 'ListItem', position: 2, name: '全部追踪', item: `${origin}/tracking` },
      { '@type': 'ListItem', position: 3, name: topic.title, item: pageUrl },
    ],
  };

  return (
    <>
      <Seo
        title={`${topic.title} - 最新进展与追踪时间线 | 看料社`}
        description={`${topic.summary} 本篇持续跟进最新动态与进展节点，更新时间 ${topic.updatedAt}。`}
        keywords={`${topic.tags.join(',')},${topic.title},追踪,最新进展`}
        jsonLd={[articleJsonLd, breadcrumbJsonLd]}
      />

      <section className="border-b border-background-200 bg-background-100 py-8 md:py-12">
        <Container>
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '全部追踪', path: '/tracking' },
              { label: topic.categoryName, path: `/tracking?cat=${topic.category}` },
              { label: topic.title },
            ]}
          />

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1 rounded-full bg-primary-600 px-3 py-1 text-[11px] font-medium text-background-50">
              <i className="ri-price-tag-3-line text-xs"></i>
              {topic.categoryName}
            </span>
            <StatusBadge status={topic.status} />
          </div>

          <h1 className="mt-4 max-w-4xl font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
            {topic.title}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-600 md:text-[13px]">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              首次记录 {topic.publishedAt}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-refresh-line text-primary-600"></i>
              最近更新 {topic.updatedAt}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-fire-line text-primary-500"></i>
              热度 {topic.heat}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-node-tree"></i>
              共 {topic.updates.length} 个进展节点
            </span>
          </div>
        </Container>
      </section>

      <section className="bg-background-50 py-10 md:py-14">
        <Container>
          <div className="relative h-56 w-full overflow-hidden rounded-lg md:h-[420px]">
            <img
              src={topic.cover}
              alt={`${topic.title} 追踪专题配图`}
              title={`${topic.title} - 看料社追踪`}
              className="absolute inset-0 h-full w-full object-cover object-top"
            />
          </div>

          <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-[1.6fr_1fr] lg:gap-14">
            <article>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">专题说明</h2>
              <div className="mt-5 flex flex-col gap-4">
                {topic.body.map((paragraph, index) => (
                  <p key={index} className="text-sm leading-7 text-foreground-700 md:text-[15px] md:leading-8">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-2">
                {topic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900"
                  >
                    <i className="ri-hashtag"></i>
                    {tag}
                  </span>
                ))}
              </div>

              <h2 className="mt-12 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                进展时间线
              </h2>
              <p className="mt-3 text-sm text-foreground-600">
                以下节点按时间顺序排列，最新节点在最上方，历史记录不会删除。
              </p>

              <ol className="mt-6 border-l-2 border-background-200">
                {[...topic.updates].reverse().map((update, index) => (
                  <li key={`${update.date}-${index}`} className="relative pl-8 pb-8 last:pb-0">
                    <span
                      className={`absolute -left-[7px] top-1.5 flex h-3 w-3 rounded-full border-2 border-background-50 ${
                        index === 0 ? 'bg-primary-600' : 'bg-accent-500'
                      }`}
                    ></span>
                    <div className="flex flex-wrap items-center gap-3">
                      <time className="font-heading text-sm font-bold text-foreground-950">{update.date}</time>
                      <span
                        className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                          index === 0 ? 'bg-primary-100 text-primary-800' : 'bg-background-200 text-foreground-700'
                        }`}
                      >
                        {update.tag}
                      </span>
                      {index === 0 && (
                        <span className="text-[11px] font-medium text-primary-600">最新节点</span>
                      )}
                    </div>
                    <h3 className="mt-2 font-heading text-base font-bold text-foreground-950">{update.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{update.content}</p>
                  </li>
                ))}
              </ol>
            </article>

            <aside className="flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-bookmark-3-line text-primary-600"></i>
                  关键节点
                </h2>
                <ul className="mt-4 flex flex-col gap-3">
                  {topic.highlights.map((highlight, index) => (
                    <li key={highlight} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-accent-100 font-heading text-xs font-bold text-accent-900">
                        {index + 1}
                      </span>
                      <span className="text-sm leading-relaxed text-foreground-700">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-background-200 bg-accent-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-notification-3-line text-primary-600"></i>
                  跟进这条线索
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                  订阅更新后，当本条线索出现新的可核实节点，我们会第一时间整理并通知你。
                </p>
                <Link
                  to="/about#contact"
                  className="mt-4 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
                >
                  <i className="ri-mail-send-line"></i>
                  订阅进展提醒
                </Link>
              </div>
            </aside>
          </div>
        </Container>
      </section>

      <section className="border-t border-background-200 bg-background-100 py-14 md:py-16">
        <Container>
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关推荐</h2>
            <Link
              to="/tracking"
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
            >
              查看全部追踪
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {recommend.map((item) => (
              <TopicCard key={item.slug} topic={item} />
            ))}
          </div>
        </Container>
      </section>
    </>
  );
}